// Fullscreen API with the webkit prefix Chrome 58's WebView still needs.

type PrefixedElement = HTMLElement & { webkitRequestFullscreen?: () => void };
type PrefixedDocument = Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };

export function fullscreenElement(): Element | null {
  const doc = document as PrefixedDocument;
  return doc.fullscreenElement || doc.webkitFullscreenElement || null;
}

export function fullscreenSupported(): boolean {
  const node = document.documentElement as PrefixedElement;
  return typeof node.requestFullscreen === 'function' || typeof node.webkitRequestFullscreen === 'function';
}

/** Enters fullscreen on `node`, or leaves it when something is already fullscreen. */
export function toggleFullscreen(node: HTMLElement): void {
  const doc = document as PrefixedDocument;
  if (fullscreenElement()) {
    if (typeof doc.exitFullscreen === 'function') void Promise.resolve(doc.exitFullscreen()).catch(() => undefined);
    else if (typeof doc.webkitExitFullscreen === 'function') doc.webkitExitFullscreen();
    return;
  }
  const target = node as PrefixedElement;
  if (typeof target.requestFullscreen === 'function') void Promise.resolve(target.requestFullscreen()).catch(() => undefined);
  else if (typeof target.webkitRequestFullscreen === 'function') target.webkitRequestFullscreen();
}

/** Subscribes to fullscreen changes under both event names; returns the unsubscribe. */
export function onFullscreenChange(listener: () => void): () => void {
  document.addEventListener('fullscreenchange', listener);
  document.addEventListener('webkitfullscreenchange', listener);
  return () => {
    document.removeEventListener('fullscreenchange', listener);
    document.removeEventListener('webkitfullscreenchange', listener);
  };
}
